import { portalText, usePortalTranslation } from "../../i18n/portal.js";
/**
 * `data-screen-label="Audit log"` — plan unit P14-06, over RB-29's two operations
 * (`listAuditEntries`, `exportAuditEntries`; design §15.6.9); the organization log, the filters and each entry's
 * provenance added by plan-014 C4b (design §27.9.2 `audit-entry`; FR12-SEC-08).
 *
 * WHAT IS ON IT: the kicker (workspace — or organization — · how long entries are kept), the filter over the five
 * event classes plus "Everything", the count note, the When / Who / What they did / Where from table, older pages
 * through `next_cursor`, and "Export CSV". Since plan-014 C4b, beneath the headings:
 *
 *   · "This workspace" / "Whole organization" — offered only to a person whose organization role lists
 *     `account.audit.read` or `account.audit.financial_read` (the API decides; Finance is told it sees financial
 *     entries only);
 *   · "Who" (the people named on the entries read so far, as the real or the effective actor), "Result", and a
 *     "From" / "To" window, with "Clear filters";
 *   · on each row, the person acted AS (an operator acting as a member, a key acting for its owner), a "Refused" or
 *     "Failed" tag, and "Details" — what the action was about, the reason given and the safe fields before → after.
 *
 * FOUR THINGS THIS SCREEN IS CAREFUL ABOUT.
 *
 * **1. A WeTalk operator is never shown as the member (AC-33).** An operator row names the
 * operator the entry recorded, carries a "WeTalk operator" tag, and says whom the operator was
 * acting as — by name when the entry records them, as "a member" when an older one does not. When the entry
 * recorded no operator name it says exactly that and never borrows the member's. An API key's row is tagged "API
 * key" and is never presented as a person. See `audit-entry-copy.ts`.
 *
 * **2. "Nothing here" has two sentences.** An empty log and a filter that matches nothing are
 * different facts; the second offers the way back to everything.
 *
 * **3. Older entries are appended, not swapped in.** Each page is its own query keyed by its
 * cursor and its filters, so "Show older entries" adds a page beneath the ones already read and a refetch refreshes
 * each page in place. Changing any filter starts again from the newest.
 *
 * **4. The export is a round trip and is itself audited.** "Export CSV" asks the server for a
 * file of exactly what the filters select; the link appears only once the gateway has answered with one, and only
 * while the filters are still the ones it was asked for. The export writes `audit_entry.exported`, so the
 * list is re-read afterwards and the export shows up in the log it exported.
 *
 * A refusal is printed with its own message rather than swallowed: a screen that caught a
 * failure into an empty table would read as a workspace in which nothing has happened.
 */
import { queryOptions, useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Button,
  Field,
  Icon,
  Input,
  Segmented,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
  Tag,
} from "@wetalk/ui";
import { useState, type ReactNode } from "react";

import {
  ACTOR_TAG_TEXT,
  FILTER_OPTION,
  LOG_OPTION,
  RESULT_OPTION,
  RESULT_TAG_TEXT,
  actorTextOf,
  dayEndOf,
  dayStartOf,
  entryNoteText,
  eventClassOfFilter,
  exportReadyText,
  filterLabelOf,
  filterValueOf,
  kickerText,
  organizationKickerText,
  peopleOf,
  rowGlyphOf,
  type AuditEntryScopeChoice,
} from "./audit-entry-copy.js";
import { useCurrentWorkspace } from "../../shell/current-workspace.js";
import { GLYPH } from "../../shell/glyph.js";
import { useGateway } from "../../api/provider.js";
import { QUERY_KEY, authContextQuery } from "../../api/query.js";
import { NO_AUDIT_FILTER } from "../../api/governance-view.js";
import type {
  AuditEntryRowView,
  AuditEventClassView,
  AuditFilterView,
  AuditEntryScopeView,
  AuditQueryView,
  AuditResultView,
  ConsoleGateway,
  ExportArtefactView,
} from "../../api/gateway.js";

import "./audit-entry.css";

export interface AuditEntryScreenProps {
  /** `null` is "Everything" — the absence of a filter, not a fifth value. */
  readonly eventClass: AuditEventClassView | null;
  onEventClass(next: AuditEventClassView | null): void;
}

/** What the screen's filters select, beyond the heading the router carries. */
interface Selection {
  readonly log: AuditEntryScopeChoice;
  readonly filter: AuditFilterView;
  /** The date inputs as typed, so a half-typed date neither filters nor is lost. */
  readonly fromDay: string;
  readonly toDay: string;
}

const NO_SELECTION: Selection = { log: "workspace", filter: NO_AUDIT_FILTER, fromDay: "", toDay: "" };

/** The cursors read so far under one selection; `null` is the newest page. */
interface CursorTrail {
  readonly key: string;
  readonly cursor: readonly (string | null)[];
}

/** One page of one selection, keyed by everything that selects it. */
function pageQuery(gateway: ConsoleGateway, query: AuditQueryView) {
  return queryOptions({
    queryKey: [...QUERY_KEY.auditEntry, query.eventClass ?? "everything", query.log ?? { kind: "workspace" }, query.filter ?? NO_AUDIT_FILTER, query.cursor ?? "first"],
    queryFn: () => gateway.listAuditEntries(query),
  });
}

function filterSet(filter: AuditFilterView): boolean {
  return filter.actorPersonId !== null || filter.result !== null || filter.from !== null || filter.to !== null;
}

export function AuditEntryScreen({ eventClass, onEventClass }: AuditEntryScreenProps): ReactNode {
  usePortalTranslation("finance");
  const gateway = useGateway();
  const queryClient = useQueryClient();
  // The whole context, not only its workspace arm (INT for C4b, 2026-10-06): with the organization alone open
  // (Finance's normal state) there is no workspace log, and the organization's is the only one.
  const authContext = useQuery(authContextQuery(gateway));
  const context = authContext.data;
  const inOrganization = context === undefined || context === null || context.kind === "selection_required" ? null : context;
  const organizationAlone = inOrganization?.kind === "account_only";
  const currentWorkspace = useCurrentWorkspace();

  // The organization log is offered to an organization role that lists either audit capability; the API decides.
  const accountCapability = inOrganization?.capability.account ?? [];
  const mayReadOrganization = accountCapability.includes("account.audit.read") || accountCapability.includes("account.audit.financial_read");
  const accountId = inOrganization?.accountId ?? null;

  const [selection, setSelection] = useState<Selection>(NO_SELECTION);
  const log: AuditEntryScopeView =
    (organizationAlone || selection.log === "organization") && mayReadOrganization && accountId !== null
      ? { kind: "organization", accountId }
      : { kind: "workspace" };
  const base: Omit<AuditQueryView, "cursor"> = { eventClass, log, filter: selection.filter };
  const key = JSON.stringify(base);

  const [trail, setTrail] = useState<CursorTrail>({ key, cursor: [null] });
  // A trail read under another selection is not this selection's: start again from the newest.
  const cursor = trail.key === key ? trail.cursor : [null];

  const page = useQueries({ queries: cursor.map((one) => pageQuery(gateway, { ...base, cursor: one })) });
  const first = page[0];
  const last = page[page.length - 1];
  const lastData = last?.data;
  const rowsRead = page.flatMap((one) => one.data?.row ?? []);

  const exportCsv = useMutation<ExportArtefactView, Error, AuditQueryView>({
    mutationFn: (query) => gateway.exportAuditEntriesCsv(query),
    onSuccess: async () => {
      // `audit_entry.exported` was just written; re-read so it appears in this log.
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY.auditEntry });
    },
  });
  // A link prepared under another selection is not a file of what is on screen now.
  const exportForThisSelection = exportCsv.variables !== undefined && JSON.stringify({ ...exportCsv.variables, cursor: undefined }) === JSON.stringify({ ...base, cursor: undefined });

  const workspaceName = currentWorkspace.workspace?.name ?? null;
  const retentionMonth = first?.data?.retentionMonth ?? null;
  const kicker =
    log.kind === "organization"
      ? organizationKickerText(inOrganization?.accountName ?? null, retentionMonth)
      : kickerText(inOrganization?.kind === "workspace_selected" ? workspaceName : null, retentionMonth);

  const setFilter = (next: Partial<AuditFilterView>, typed: Partial<Pick<Selection, "fromDay" | "toDay">> = {}): void => {
    setSelection((now) => ({ ...now, ...typed, filter: { ...now.filter, ...next } }));
  };
  const people = peopleOf(rowsRead);
  const chosenPerson = selection.filter.actorPersonId;
  const personOption = chosenPerson !== null && !people.some((one) => one.personId === chosenPerson) ? [...people, { personId: chosenPerson, name: portalText("finance", "audit.chosenPerson") }] : people;

  return (
    <section className="c-page aud-page">
      <div className="c-headline-row">
        <div>
          <p className="aud-kicker">{kicker}</p>
          <h1 className="aud-title">{portalText("finance", "m1103")}</h1>
        </div>
        <Button
          variant="secondary"
          disabled={exportCsv.isPending}
          onClick={() => {
            exportCsv.mutate({ ...base, cursor: null });
          }}
        >
          <Icon glyph={GLYPH.downloadSimple} size="md" />
          {exportCsv.isPending ? portalText("finance", "m1104") : portalText("finance", "m0287")}
        </Button>
      </div>

      {exportForThisSelection && exportCsv.isError ? (
        <p className="c-note c-note-critical">{portalText("finance", "m1105")}{" "}
          {exportCsv.error.message}
        </p>
      ) : null}
      {exportForThisSelection && exportCsv.data !== undefined ? (
        <div className="c-panel c-panel-accent aud-export">
          <span className="aud-export-text">
            {exportReadyText(exportCsv.data.rowCount, eventClass, exportCsv.data.expiresAtText)}
          </span>
          <a className="btn btn-primary" href={exportCsv.data.downloadUrl} download>{portalText("finance", "m1041")}{exportCsv.data.fileExtension}
          </a>
        </div>
      ) : null}

      <div className="aud-filters">
        <Segmented
          name="auditFilter"
          legend={portalText("finance", "m1193")}
          value={filterValueOf(eventClass)}
          options={[...FILTER_OPTION]}
          onChange={(next) => {
            onEventClass(eventClassOfFilter(next));
          }}
        />
        {first?.data === undefined ? null : (
          <span className="aud-note">{entryNoteText(first.data.matchedCount, first.data.matchedCountCapped, first.data.financeOnly)}</span>
        )}
      </div>

      <div className="aud-refine">
        {mayReadOrganization && !organizationAlone ? (
          <Segmented
            name="auditLog"
            legend={portalText("finance", "m1194")}
            value={log.kind === "organization" ? "organization" : "workspace"}
            options={[...LOG_OPTION]}
            onChange={(next) => {
              setSelection((now) => ({ ...now, log: next }));
            }}
          />
        ) : null}
        <div className="aud-refine-field">
          <Field label={portalText("finance", "m1106")}>
            {(control) => (
              <Select
                {...control}
                value={chosenPerson ?? "anyone"}
                onChange={(event) => {
                  setFilter({ actorPersonId: event.target.value === "anyone" ? null : event.target.value });
                }}
              >
                <option value="anyone">{portalText("finance", "m1107")}</option>
                {personOption.map((one) => (
                  <option key={one.personId} value={one.personId}>
                    {one.name}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        </div>
        <div className="aud-refine-field">
          <Field label={portalText("finance", "m1108")}>
            {(control) => (
              <Select
                {...control}
                value={selection.filter.result ?? "any"}
                onChange={(event) => {
                  const value = event.target.value;
                  setFilter({ result: value === "any" ? null : (value as AuditResultView) });
                }}
              >
                {RESULT_OPTION.map((one) => (
                  <option key={one.value} value={one.value}>
                    {one.label}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        </div>
        <div className="aud-refine-field">
          <Field label={portalText("finance", "m0575")}>
            {(control) => (
              <Input
                {...control}
                type="date"
                value={selection.fromDay}
                onChange={(event) => {
                  setFilter({ from: dayStartOf(event.target.value) }, { fromDay: event.target.value });
                }}
              />
            )}
          </Field>
        </div>
        <div className="aud-refine-field">
          <Field label={portalText("finance", "m0577")}>
            {(control) => (
              <Input
                {...control}
                type="date"
                value={selection.toDay}
                onChange={(event) => {
                  setFilter({ to: dayEndOf(event.target.value) }, { toDay: event.target.value });
                }}
              />
            )}
          </Field>
        </div>
        {filterSet(selection.filter) ? (
          <button
            type="button"
            className="aud-clear"
            onClick={() => {
              setSelection((now) => ({ ...NO_SELECTION, log: now.log }));
            }}
          >{portalText("finance", "m1109")}</button>
        ) : null}
      </div>

      {first === undefined || first.isPending ? (
        <p className="c-note">{portalText("finance", "m1110")}</p>
      ) : first.isError ? (
        <p className="c-note c-note-critical">{portalText("finance", "m1111")}{" "}
          {first.error.message}
        </p>
      ) : first.data.row.length === 0 ? (
        <NothingHere
          eventClass={eventClass}
          filtered={filterSet(selection.filter)}
          organization={log.kind === "organization"}
          retentionMonth={first.data.retentionMonth}
          onEverything={() => {
            setSelection((now) => ({ ...NO_SELECTION, log: now.log }));
            onEventClass(null);
          }}
        />
      ) : (
        <>
          <div className="aud-scroll">
            <Table caption={log.kind === "organization" ? portalText("finance", "m1112") : portalText("finance", "m1113")}>
              <TableHead>
                <TableRow>
                  <TableHeaderCell>{portalText("finance", "m0736")}</TableHeaderCell>
                  <TableHeaderCell>{portalText("finance", "m1106")}</TableHeaderCell>
                  <TableHeaderCell>{portalText("finance", "m1114")}</TableHeaderCell>
                  <TableHeaderCell>{portalText("finance", "m1115")}</TableHeaderCell>
                </TableRow>
              </TableHead>
              {page.map((one, index) =>
                one.data === undefined ? null : (
                  <TableBody key={cursor[index] ?? "first"}>
                    {one.data.row.map((row) => (
                      <EntryRow key={row.auditEntryId} row={row} />
                    ))}
                  </TableBody>
                ),
              )}
            </Table>
          </div>

          {last === undefined || last === first ? null : last.isPending ? (
            <p className="c-note">{portalText("finance", "m1116")}</p>
          ) : last.isError ? (
            <p className="c-note c-note-critical">{portalText("finance", "m1117")}{last.error.message}{" "}
              <button
                type="button"
                className="aud-clear"
                onClick={() => {
                  void last.refetch();
                }}
              >{portalText("finance", "m0382")}</button>
            </p>
          ) : null}

          {lastData === undefined ? null : lastData.nextCursor === null ? (
            <p className="aud-end">{portalText("finance", "m1118")}{String(lastData.retentionMonth)}{portalText("finance", "m1119")}</p>
          ) : (
            <div className="c-row aud-more">
              <Button
                variant="secondary"
                onClick={() => {
                  setTrail({ key, cursor: [...cursor, lastData.nextCursor] });
                }}
              >{portalText("finance", "m1120")}</Button>
            </div>
          )}
        </>
      )}
    </section>
  );
}

function EntryRow({ row }: { readonly row: AuditEntryRowView }): ReactNode {
  usePortalTranslation("finance");
  const actor = actorTextOf(row);
  const hasDetail = row.reason !== null || row.change.length > 0 || row.scopeText !== "Workspace";
  return (
    <TableRow>
      <TableCell>
        <span className="aud-when">{row.whenText}</span>
      </TableCell>
      <TableCell>
        <span className="aud-who">
          <span className="aud-who-name">{actor.name}</span>
          {actor.tag === null ? null : <Tag tone="outline">{ACTOR_TAG_TEXT[actor.tag]}</Tag>}
        </span>
        {actor.onBehalfText === null ? null : (
          <span className="aud-behalf">{actor.onBehalfText}</span>
        )}
      </TableCell>
      <TableCell>
        <span className="aud-what">
          <Icon glyph={rowGlyphOf(row.iconName)} size="lg" tone="accent" />
          <span className="aud-what-text">
            {row.summary}
            {row.result === "succeeded" ? null : (
              <>
                {" "}
                <Tag tone="outline" status={row.result === "refused" ? "caution" : "critical"}>
                  {RESULT_TAG_TEXT[row.result]}
                </Tag>
              </>
            )}
          </span>
        </span>
        {hasDetail ? (
          <details className="aud-detail">
            <summary className="aud-detail-toggle">{portalText("finance", "m1121")}</summary>
            <dl className="aud-detail-list">
              <dt>{portalText("finance", "m1122")}</dt>
              <dd>{row.scopeText}</dd>
              {row.reason === null ? null : (
                <>
                  <dt>{portalText("finance", "m1123")}</dt>
                  <dd>{row.reason}</dd>
                </>
              )}
              {row.change.map((one) => (
                <ChangeLine key={one.field} field={one.field} beforeText={one.beforeText} afterText={one.afterText} />
              ))}
            </dl>
          </details>
        ) : null}
      </TableCell>
      <TableCell>
        <span className="aud-from">{row.fromText}</span>
      </TableCell>
    </TableRow>
  );
}

function ChangeLine({
  field,
  beforeText,
  afterText,
}: {
  readonly field: string;
  readonly beforeText: string | null;
  readonly afterText: string | null;
}): ReactNode {
  usePortalTranslation("finance");
  return (
    <>
      <dt className="aud-detail-field">{field}</dt>
      <dd>
        {beforeText === null ? portalText("finance", "m1265") : <span className="aud-detail-before">{beforeText}</span>}
        {" → "}
        {afterText === null ? portalText("finance", "m1266") : <span className="aud-detail-after">{afterText}</span>}
      </dd>
    </>
  );
}

function NothingHere({
  eventClass,
  filtered,
  organization,
  retentionMonth,
  onEverything,
}: {
  readonly eventClass: AuditEventClassView | null;
  readonly filtered: boolean;
  readonly organization: boolean;
  readonly retentionMonth: number;
  readonly onEverything: () => void;
}): ReactNode {
  usePortalTranslation("finance");
  const months = String(retentionMonth);
  if (eventClass === null && !filtered) {
    return (
      <div className="c-panel c-panel-dashed">
        <p className="c-empty-title">{portalText("finance", "m1124")}</p>
        <p className="c-empty-dek">{portalText("finance", organization ? "audit.empty.organization" : "audit.empty.workspace", { months })}</p>
      </div>
    );
  }
  return (
    <div className="c-panel c-panel-dashed">
      <p className="c-empty-title">
        {eventClass === null ? portalText("finance", "m1127") : portalText("finance", "m1128", { value1: filterLabelOf(eventClass) })}{portalText("finance", "m1129")}{months}{portalText("finance", "m1130")}</p>
      <p className="c-empty-dek">{portalText("finance", "m1131")}{" "}
        <button type="button" className="aud-clear" onClick={onEverything}>{portalText("finance", "m1132")}</button>
      </p>
    </div>
  );
}
